import Box from "@mui/material/Box";
import { useCallback } from "react";
import { useDispatch } from "react-redux";

import {
  secondaryOrderId,
  secondaryValuationOrdersApi,
} from "../../api/secondaryValuationOrdersApi";
import { loanDrilldownSetKey } from "../../redux/actions/loanDrilldownActions";
import { status as statusTokens } from "../../theme/colors";

/* Shared vocabulary + chip for SECONDARY valuation orders (the outside-party
   orders on the Orders tab). The backend statuses are verbatim
   (`SecondaryOrderStatus`); only the labels are friendlier. */

/** Redux key on the loanDrilldown slice holding this loan's secondary orders. */
export const SECONDARY_ORDERS_KEY = "secondaryValuationOrders";

export const SECONDARY_STATUS_META = {
  requested: { label: "Requested", tone: statusTokens.neutral },
  submitted: { label: "Placed with vendor", tone: statusTokens.info },
  received: { label: "Received", tone: statusTokens.success },
  tied_out: { label: "Tied out", tone: statusTokens.success },
  cancelled: { label: "Cancelled", tone: statusTokens.error },
};

export const secondaryStatusMeta = (s) =>
  SECONDARY_STATUS_META[s] || { label: s || "—", tone: statusTokens.neutral };

// Same pill shape as the internal review's AppraisalStatusChip so the rows of
// one card read alike.
export const SecondaryStatusChip = ({ status }) => {
  const m = secondaryStatusMeta(status);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        fontSize: 11,
        fontWeight: 500,
        color: m.tone.fg,
        background: m.tone.bg,
        borderRadius: "6px",
        px: "9px",
        py: "3px",
        whiteSpace: "nowrap",
      }}
    >
      <Box
        component="span"
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: m.tone.solid,
        }}
      />
      {m.label}
    </Box>
  );
};

/** Orders the Orders tab treats as "outside party" (everything but internal —
 *  internal reviews are listed from the internal appraisal queue instead). */
export const isExternalSecondary = (o) => o && o.kind !== "internal";

/**
 * Splice one returned order into the slice (replace by id, else append), read
 * at dispatch time so a concurrent refresh isn't overwritten.
 */
export const upsertSecondaryOrder = (order) => (dispatch, getState) => {
  const id = secondaryOrderId(order);
  if (!id) return;
  const current = getState()?.loanDrilldown?.[SECONDARY_ORDERS_KEY];
  const list = Array.isArray(current) ? current : [];
  const idx = list.findIndex((o) => secondaryOrderId(o) === id);
  const next =
    idx >= 0
      ? list.map((o, i) => (i === idx ? order : o))
      : [...list, order];
  dispatch(loanDrilldownSetKey({ key: SECONDARY_ORDERS_KEY, value: next }));
};

/** `refresh(dealId)` re-reads the loan's secondary orders into the slice. */
export const useRefreshSecondaryOrders = () => {
  const dispatch = useDispatch();
  return useCallback(
    (dealId) => {
      if (!dealId) return Promise.resolve([]);
      return secondaryValuationOrdersApi
        .list(dealId)
        .then((orders) => {
          dispatch(
            loanDrilldownSetKey({ key: SECONDARY_ORDERS_KEY, value: orders }),
          );
          return orders;
        })
        .catch(() => {
          // First load failed → park an empty list so the tab doesn't refetch
          // in a loop. A failed RE-read keeps what is already on screen.
          dispatch((d, getState) => {
            const current = getState()?.loanDrilldown?.[SECONDARY_ORDERS_KEY];
            if (current == null) {
              d(loanDrilldownSetKey({ key: SECONDARY_ORDERS_KEY, value: [] }));
            }
          });
          return [];
        });
    },
    [dispatch],
  );
};
